/**
 * Client-side AutoMapa WhatsApp widget rollout flags (M12).
 * Mirrors server env names via REACT_APP_* where applicable.
 * Fail closed in production builds unless explicitly enabled.
 */

export const AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS = Object.freeze({
  WIDGET: "automapaWhatsappWidgetEnabled",
  VEHICLE_CTA: "automapaWhatsappVehicleCtaWidgetEnabled",
  STRUCTURED_INTAKE: "automapaWhatsappStructuredIntakeEnabled",
  LEAD_BROKER_SUBMIT: "automapaWhatsappLeadBrokerSubmitEnabled",
  CONTINUE: "automapaWhatsappContinueEnabled",
  STATUS: "automapaWhatsappStatusEnabled",
  PERSISTENCE: "automapaWhatsappPersistenceEnabled",
  ADMIN_MONITORING: "automapaWhatsappAdminMonitoringEnabled",
  CHAT_BFF: "automapaWhatsappChatBffEnabled",
  WEBHOOK_SYNC: "automapaWhatsappWebhookSyncEnabled",
  BOT_INTAKE: "automapaWhatsappBotIntakeEnabled",
});

export const AUTOMAPA_WHATSAPP_WIDGET_DISABLED_COPY = Object.freeze({
  widgetUnavailable:
    "El asistente de WhatsApp no está disponible en este momento. Puedes continuar por WhatsApp.",
  chatUnavailable:
    "El chat en la web no está disponible ahora. Completa el formulario o continúa en WhatsApp.",
  botUnavailable:
    "El chat asistido no está disponible. Usa el formulario rápido o continúa en WhatsApp.",
  syncUnavailable:
    "La conversación de WhatsApp puede tardar unos segundos en actualizarse.",
  brokerUnavailable:
    "Registramos tu consulta en AutoMapa. El envío a WhatsApp puede no estar disponible aún.",
});

function parseBooleanEnv(value, fallback) {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "off"].includes(normalized)) return false;
  return fallback;
}

function parseCsv(value) {
  if (!value) return [];
  return String(value)
    .split(/[,;\n]/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function isProductionBuild() {
  return process.env.NODE_ENV === "production";
}

export function isAutomapaWhatsappWidgetGloballyEnabled() {
  if (process.env.REACT_APP_AUTOMAPA_WHATSAPP_WIDGET_ENABLED === "false") {
    return false;
  }
  if (process.env.REACT_APP_AUTOMAPA_WHATSAPP_WIDGET_ENABLED === "true") {
    return true;
  }
  return !isProductionBuild();
}

function subFlag(envName, { defaultWhenMasterOn, failClosedInProduction = false } = {}) {
  if (!isAutomapaWhatsappWidgetGloballyEnabled()) {
    return false;
  }
  const fallback =
    failClosedInProduction && isProductionBuild() ? false : Boolean(defaultWhenMasterOn);
  return parseBooleanEnv(process.env[envName], fallback);
}

export function resolveAutomapaWhatsappWidgetFlags() {
  const widgetEnabled = isAutomapaWhatsappWidgetGloballyEnabled();
  if (!widgetEnabled) {
    return {
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.WIDGET]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.VEHICLE_CTA]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.STRUCTURED_INTAKE]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.LEAD_BROKER_SUBMIT]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.CONTINUE]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.STATUS]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.PERSISTENCE]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.ADMIN_MONITORING]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.CHAT_BFF]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.WEBHOOK_SYNC]: false,
      [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.BOT_INTAKE]: false,
    };
  }

  return {
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.WIDGET]: true,
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.VEHICLE_CTA]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_VEHICLE_CTA_WIDGET_ENABLED",
      { defaultWhenMasterOn: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.STRUCTURED_INTAKE]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_STRUCTURED_INTAKE_ENABLED",
      { defaultWhenMasterOn: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.LEAD_BROKER_SUBMIT]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_LEAD_BROKER_SUBMIT_ENABLED",
      { defaultWhenMasterOn: true, failClosedInProduction: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.CONTINUE]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_CONTINUE_ENABLED",
      { defaultWhenMasterOn: false }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.STATUS]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_STATUS_ENABLED",
      { defaultWhenMasterOn: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.PERSISTENCE]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_PERSISTENCE_ENABLED",
      { defaultWhenMasterOn: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.ADMIN_MONITORING]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_ADMIN_MONITORING_ENABLED",
      { defaultWhenMasterOn: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.CHAT_BFF]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_CHAT_BFF_ENABLED",
      { defaultWhenMasterOn: true, failClosedInProduction: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.WEBHOOK_SYNC]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_WEBHOOK_SYNC_ENABLED",
      { defaultWhenMasterOn: true, failClosedInProduction: true }
    ),
    [AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.BOT_INTAKE]: subFlag(
      "REACT_APP_AUTOMAPA_WHATSAPP_BOT_INTAKE_ENABLED",
      { defaultWhenMasterOn: true, failClosedInProduction: true }
    ),
  };
}

export function isAutomapaWhatsappCapabilityEnabled(flagKey) {
  return Boolean(resolveAutomapaWhatsappWidgetFlags()[flagKey]);
}

export function getAutomapaWhatsappWidgetPageAllowlist() {
  return parseCsv(process.env.REACT_APP_AUTOMAPA_WHATSAPP_WIDGET_PAGE_ALLOWLIST);
}

/**
 * Map a runtime pathname to a coarse page key for page-level rollout.
 */
export function resolveAutomapaWhatsappWidgetPageKey(pathname = "") {
  const path = String(pathname || "").toLowerCase();
  if (!path || path === "/" || path.endsWith("/automap") || /\/automap\/?$/.test(path)) {
    return "home";
  }
  if (path.includes("/search") || path.includes("/buscar")) return "search";
  if (path.includes("/vehicle") || path.includes("/vehiculo") || path.includes("/listing")) {
    return "vehicle_detail";
  }
  if (path.includes("/preferences") || path.includes("/preferencias")) return "preferences";
  if (path.includes("/account") || path.includes("/cuenta") || path.includes("/saved")) {
    return "account";
  }
  return "home";
}

export function isAutomapaWhatsappWidgetEnabledForPage(pageKeyOrPath) {
  if (!isAutomapaWhatsappCapabilityEnabled(AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.WIDGET)) {
    return false;
  }
  const allowlist = getAutomapaWhatsappWidgetPageAllowlist();
  if (allowlist.length === 0) return true;
  const raw = String(pageKeyOrPath || "");
  const resolved = raw.includes("/")
    ? resolveAutomapaWhatsappWidgetPageKey(raw)
    : raw.toLowerCase();
  return allowlist.includes(resolved);
}

export function shouldPreferAutomapaWhatsappWidget({ pagePath = null } = {}) {
  if (!isAutomapaWhatsappCapabilityEnabled(AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.WIDGET)) {
    return false;
  }
  if (!isAutomapaWhatsappCapabilityEnabled(AUTOMAPA_WHATSAPP_WIDGET_FLAG_KEYS.VEHICLE_CTA)) {
    return false;
  }
  if (pagePath != null && pagePath !== "" && !isAutomapaWhatsappWidgetEnabledForPage(pagePath)) {
    return false;
  }
  return true;
}
